import React, { useMemo, useState } from "react"
import { Plus } from "lucide-react"

const StatusBadge = ({ label, statusClass }) => (
  <span className={`inline-block px-2 py-1 rounded text-xs font-medium ${statusClass}`}>
    {label}
  </span>
)

const SiteVisitsAdminList = ({
  adminStaff = [],
  loading = false,
  statusByEmail = {},
  statusesLoading = false,
  currentYear,
  getStatusLabel,
  getStatusClass,
  onInitiateSiteVisit,
  onCaptureObservations,
  onCompleteEvaluation,
  onReinitiateSiteVisit,
  onViewSiteVisit,
}) => {
  const [searchQuery, setSearchQuery] = useState("")

  const sortNewestFirst = (a, b) => {
    const aTime = a?.createdAt ? new Date(a.createdAt).getTime() : Number(a?.id || 0)
    const bTime = b?.createdAt ? new Date(b.createdAt).getTime() : Number(b?.id || 0)
    return bTime - aTime
  }

  const filteredStaff = useMemo(() => {
    const query = searchQuery.trim().toLowerCase()
    if (!query) return adminStaff

    return adminStaff.filter((admin) => {
      const fullName = `${admin.firstName || ""} ${admin.lastName || ""}`.toLowerCase()
      const email = (admin.email || "").toLowerCase()
      return fullName.includes(query) || email.includes(query)
    })
  }, [adminStaff, searchQuery])

  const getAdminStatus = (admin) => {
    const data = statusByEmail[admin.email?.toLowerCase()] || { siteVisits: [] }
    const visitsThisYear = (data.siteVisits || [])
      .filter((item) => item.year === currentYear)
      .sort(sortNewestFirst)

    const latestVisit = visitsThisYear[0] || null
    const latestActionableVisit =
      visitsThisYear.find(
        (v) => v.status !== "completed"
      ) || null

    const activeCount = visitsThisYear.filter(
      (v) => v.status !== "completed"
    ).length

    return { latestVisit, latestActionableVisit, activeCount }
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <h3 className="font-semibold text-[#241763]">Admin Staff</h3>
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search by name or email..."
          className="w-full sm:w-72 p-2 border rounded text-sm"
          aria-label="Search admin staff"
        />
      </div>

      <div className="border rounded-lg overflow-x-auto">
        <table className="w-full text-sm min-w-[760px]">
          <thead className="bg-gray-100 font-bold">
            <tr>
              <th className="p-3 text-left">Name</th>
              <th className="p-3 text-left">Email</th>
              <th className="p-3 text-left">Observation / Site Visit</th>
            </tr>
          </thead>
          <tbody>
            {loading || statusesLoading ? (
              <tr>
                <td colSpan={3} className="p-4 text-center text-gray-500">
                  Loading admin staff and statuses...
                </td>
              </tr>
            ) : filteredStaff.length === 0 ? (
              <tr>
                <td colSpan={3} className="p-4 text-center text-gray-500">
                  No admin staff found.
                </td>
              </tr>
            ) : (
              filteredStaff.map((admin) => {
                const status = getAdminStatus(admin)

                return (
                  <tr key={admin.id} className="border-t align-top hover:bg-gray-50">
                    <td className="p-3 font-medium whitespace-nowrap">
                      {admin.firstName} {admin.lastName}
                    </td>
                    <td className="p-3 text-gray-600">{admin.email}</td>
                    <td className="p-3">
                      <div className="flex flex-col gap-2 items-start">
                        <StatusBadge
                          label={
                            status.latestVisit
                              ? getStatusLabel(status.latestVisit.status)
                              : "Not Started"
                          }
                          statusClass={
                            status.latestVisit
                              ? getStatusClass(status.latestVisit.status)
                              : "bg-gray-100 text-gray-600"
                          }
                        />

                        <div className="flex flex-col gap-2 items-start">
                          <button
                            type="button"
                            onClick={() => onInitiateSiteVisit(admin)}
                            className="flex items-center gap-1 px-3 py-1 bg-[#4472C4] text-white rounded text-xs hover:opacity-90"
                          >
                            <Plus className="h-3 w-3" />
                            {status.latestVisit ? "Initiate Another Site Visit" : "Initiate Site Visit"}
                          </button>

                          {status.activeCount > 1 && (
                            <span className="text-xs text-gray-500">
                              Active visits this year: {status.activeCount}
                            </span>
                          )}
                        </div>

                        {status.latestActionableVisit?.status === "initiated" && (
                          <button
                            type="button"
                            onClick={() =>
                              onCaptureObservations(admin, status.latestActionableVisit)
                            }
                            className="px-3 py-1 bg-[#ED7D31] text-white rounded text-xs hover:opacity-90"
                          >
                            Capture Observation Evidence
                          </button>
                        )}
                        {status.latestActionableVisit?.status === "observations_captured" && (
                          <span className="text-xs text-gray-500">
                            Waiting for admin to complete Post-Observation
                          </span>
                        )}
                        {status.latestActionableVisit?.status === "submitted_by_admin" && (
                          <button
                            type="button"
                            onClick={() =>
                              onCompleteEvaluation(admin, status.latestActionableVisit)
                            }
                            className="px-3 py-1 bg-[#241763] text-white rounded text-xs hover:bg-[#1a1047]"
                          >
                            Complete Evaluation
                          </button>
                        )}
                        {status.latestVisit?.status === "completed" && (
                          <button
                            type="button"
                            onClick={() => onViewSiteVisit(admin, status.latestVisit)}
                            className="px-3 py-1 bg-gray-200 text-gray-700 rounded text-xs hover:bg-gray-300"
                          >
                            View
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                )
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}

export default SiteVisitsAdminList
